import { useCallback, useState } from 'react';

import { Container, Keyboard, Display, Line, History, Repeat, Fold } from './Calculator.style';
import Button from './Button';
import SymbolPicker, { useDigitSymbols } from './SymbolPicker';
import { add, subtract, multiply, divide, parse, toDigits } from './rational';

const opMap = {
  '+': add,
  '-': subtract,
  '/': divide,
  '*': multiply,
};

const PROJECT_URL = 'https://bristoljon.uk/project/dozenal';

const isOperator = (token) => token && token.op !== undefined;

// Digits are always `a` and `b` internally; this swaps in the chosen symbols.
const withSymbols = (str, symbols) => str.replace(/[ab]/g, (digit) => symbols[digit]);

// Renders a computed value, overlining any recurring digits.
function Value({ value, base, symbols }) {
  const { negative, int, frac, repeat, approx } = toDigits(value, base);
  return (
    <>
      {negative && '-'}
      {withSymbols(int, symbols)}
      {(frac || repeat) && '.'}
      {withSymbols(frac, symbols)}
      {repeat && <Repeat>{withSymbols(repeat, symbols)}</Repeat>}
      {approx && '…'}
    </>
  );
}

function Expression({ tokens, base, symbols }) {
  return tokens.map((token, i) => {
    if (isOperator(token)) return token.op;
    if (token.text !== null) return withSymbols(token.text, symbols);
    return <Value key={i} value={token.value} base={base} symbols={symbols} />;
  });
}

// Drops typed text so numbers are shown from their exact values, which keeps
// them right after a change of base.
const asValues = (tokens) => tokens.map((token) => (
  isOperator(token) ? token : { value: token.value, text: null }
));

function Calculator() {
  const [base, setBase] = useState(12);
  // Alternating numbers and operators. Numbers hold an exact rational `value`;
  // `text` is what the user is typing, or null for a computed result.
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState(null);
  // The last calculation, shown above its result.
  const [history, setHistory] = useState([]);
  const [symbols, setSymbol] = useDigitSymbols();
  // Which digit's symbol is being chosen, if any.
  const [picking, setPicking] = useState(null);
  const closePicker = useCallback(() => setPicking(null), []);

  const handleDigit = useCallback(
    (value) => {
      setError(null);
      setTokens((tokens) => {
        const last = tokens[tokens.length - 1];
        const isNumber = last && !isOperator(last);
        const prevText = (isNumber && last.text) || '';
        if (value === '.' && prevText.includes('.')) return tokens;
        const text = `${prevText}${value}`;
        const token = { text, value: parse(text, base) };
        // Typing over a computed result starts a new number.
        return isNumber ? [...tokens.slice(0, -1), token] : [...tokens, token];
      });
    },
    [base],
  );

  const handleOperator = useCallback(
    (op) => {
      setError(null);
      setTokens((tokens) => {
        if (!tokens.length) return tokens;
        const last = tokens[tokens.length - 1];
        return isOperator(last) ? [...tokens.slice(0, -1), { op }] : [...tokens, { op }];
      });
    },
    [],
  );

  const handlebase = useCallback(
    () => {
      // Values are exact, so switching base only changes how they're displayed.
      setTokens(asValues);
      setBase((base) => (base === 12 ? 10 : 12));
    },
    [],
  );

  const handleEquals = useCallback(
    () => {
      const terms = isOperator(tokens[tokens.length - 1]) ? tokens.slice(0, -1) : tokens;
      if (!terms.length) return;
      if (terms.length > 1) setHistory([...asValues(terms), { op: '=' }]);

      try {
        let sum = terms[0].value;
        for (let i = 1; i < terms.length; i += 2) {
          sum = opMap[terms[i].op](sum, terms[i + 1].value);
        }
        setTokens([{ value: sum, text: null }]);
      } catch (e) {
        setTokens([]);
        setError('Error');
      }
    },
    [tokens],
  );

  const handleClear = useCallback(
    () => {
      setTokens([]);
      setHistory([]);
      setError(null);
    },
    [],
  );

  return (
    <>
      <Container>
        <Fold href={PROJECT_URL} title="About, comments and bug reports" aria-label="Project page: about, comments and bug reports">
          ?
        </Fold>
        <Display>
          <History data-testid="history">
            <span>
              <Expression tokens={history} base={base} symbols={symbols} />
            </span>
          </History>
          <Line data-testid="display">
            <span>
              {error || <Expression tokens={tokens} base={base} symbols={symbols} />}
            </span>
          </Line>
        </Display>
        <Keyboard>
          <Button  value="a" label={symbols.a} onClick={handleDigit} onLongPress={setPicking} disabled={base !== 12}/>
          <Button  value="b" label={symbols.b} onClick={handleDigit} onLongPress={setPicking} disabled={base !== 12}/>
          <Button  value="." onClick={handleDigit}/>
          <Button type="operator" value="*" onClick={handleOperator}/>
          <Button  value="7" onClick={handleDigit}/>
          <Button  value="8" onClick={handleDigit}/>
          <Button  value="9" onClick={handleDigit}/>
          <Button type="operator" value="/" onClick={handleOperator}/>
          <Button  value="4" onClick={handleDigit}/>
          <Button  value="5" onClick={handleDigit}/>
          <Button  value="6" onClick={handleDigit}/>
          <Button type="operator" value="-" onClick={handleOperator}/>
          <Button  value="1" onClick={handleDigit}/>
          <Button  value="2" onClick={handleDigit}/>
          <Button  value="3" onClick={handleDigit}/>
          <Button type="operator" value="+" onClick={handleOperator}/>
          <Button type="operator" value="C" onClick={handleClear}/>
          <Button  value="0" onClick={handleDigit}/>
          <Button type="mode" value={base === 12 ? 'DOZ' : 'DEC'} onClick={handlebase}/>
          <Button type="operator" value="=" onClick={handleEquals}/>
        </Keyboard>
      </Container>
      {/* Outside Container, whose clipped corner would also clip the overlay. */}
      {picking && (
        <SymbolPicker digit={picking} symbols={symbols} onChoose={setSymbol} onClose={closePicker} />
      )}
    </>
  );
}

export default Calculator;
